import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, ShoppingBag } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ChatbotWidget } from "../components/ChatbotWidget";
import { customerApi } from "../lib/customerApi";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { api, formatINR, formatApiErrorDetail } from "../lib/api";
import { addToGuestCart, getGuestWishlist, removeFromGuestWishlist } from "../lib/guestStorage";

export default function Wishlist() {
    const { user } = useCustomerAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            if (user) {
                const { data } = await customerApi.get("/wishlist");
                setItems(data.items || []);
            } else {
                const ids = getGuestWishlist();
                if (!ids.length) { setItems([]); return; }
                const { data } = await api.get("/products", { params: { limit: 100 } });
                const map = new Map(data.items.map((p) => [p.id, p]));
                setItems(ids.map((id) => map.get(id)).filter(Boolean));
            }
        } catch (_e) {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        refresh();
        const handler = () => refresh();
        window.addEventListener("sukaanyas:wishlist-changed", handler);
        return () => window.removeEventListener("sukaanyas:wishlist-changed", handler);
    }, [refresh]);

    const remove = async (productId) => {
        try {
            if (user) {
                const { data } = await customerApi.delete(`/wishlist/${productId}`);
                setItems(data.items || []);
            } else {
                removeFromGuestWishlist(productId);
            }
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Could not remove");
        }
    };

    const moveToCart = async (product) => {
        try {
            if (user) {
                await customerApi.post("/cart/items", { productId: product.id, qty: 1 });
                await customerApi.delete(`/wishlist/${product.id}`).catch(() => {});
                toast.success("Moved to bag");
            } else {
                addToGuestCart(product.id, null, 1);
                removeFromGuestWishlist(product.id);
                toast.success("Moved to bag");
            }
            refresh();
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Could not move");
        }
    };

    return (
        <div className="min-h-screen bg-ivory" data-testid="wishlist-page">
            <Header />
            <main className="max-w-[1200px] mx-auto px-6 lg:px-12 py-12">
                <span className="label-eyebrow">Saved pieces</span>
                <h1 className="mt-3 font-display text-4xl sm:text-5xl text-inkDark tracking-tight">Wishlist</h1>

                {loading ? (
                    <div className="mt-10 text-inkMuted">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="mt-10 bg-beige rounded-2xl p-12 text-center" data-testid="wishlist-empty">
                        <p className="text-inkMuted">No pieces saved yet.</p>
                        <Link to="/category/sarees" className="btn-pill mt-6 inline-flex">Browse boutique</Link>
                    </div>
                ) : (
                    <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 lg:gap-8" data-testid="wishlist-grid">
                        {items.map((p) => (
                            <div key={p.id} className="group relative" data-testid={`wishlist-item-${p.slug}`}>
                                <Link to={`/product/${p.slug}`} className="block">
                                    <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-beige">
                                        <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                    </div>
                                    <div className="mt-3 font-serif text-inkDark line-clamp-1">{p.name}</div>
                                    <div className="text-inkDark text-sm mt-1">{formatINR(p.price)}</div>
                                </Link>
                                <div className="mt-3 flex gap-2">
                                    <button onClick={() => moveToCart(p)} className="flex-1 inline-flex items-center justify-center gap-1 text-xs px-3 py-2 rounded-full bg-skyblue text-inkDark hover:bg-skyblueDeep hover:text-white transition-colors" data-testid={`wishlist-move-${p.slug}`}>
                                        <ShoppingBag size={12} /> Move to bag
                                    </button>
                                    <button onClick={() => remove(p.id)} className="p-2 rounded-full bg-red-50 text-red-600" data-testid={`wishlist-remove-${p.slug}`}>
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>
            <Footer />
            <ChatbotWidget />
        </div>
    );
}
